import React, { Fragment, useContext, useEffect, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Info, RefreshCw } from "lucide-react";
import AppContext from "../../context/AppContext";
import translations from "../common/Translations";
import { Spinner } from "../../Spinner";
import { useLocation, useNavigate } from "react-router-dom";
import ReactSelector from "../common/CommonReactSelector";
import ModalCloseButton from "../common/ModalCloseButton";
import { fetchReportList } from "../../service/Task";
import ReportFailedStatus from "../common/ReportFailedStatus";
import ReportNoRecordsStatus from "../common/ReportNoRecordsStatus";
import { getReportLink, getReportStatus, isPerformanceReport, isReportInProgress, REPORT_STATUS } from "../../utils/reportStatus";

const formatRequestedAt = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  const timeZone = dateString.match(/([+\-]\d{2}:\d{2})$/)?.[1] || "UTC";
  return date.toLocaleString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
    timeZone,
  });
};

export default function Modal({
  downloadModal,
  setDownloadModal,
  downloadFiltration,
  isLoading,
  setPriority,
  priority,
  type,
  status,
  setStatus,
  keepOpenAfterDownload = false,
  // Optional caller-supplied caveat about what this export will contain,
  // rendered directly above the Download button so it is read before the
  // export is queued rather than after. Callers with nothing to say omit it.
  warningMessage = ""
}) {
  const cancelButtonRef = useRef(null);
  const { theme, langMode } = useContext(AppContext);
  const [selectedOption, setSelectedOption] = useState("all"); // Default to 'all'
  const pathname = useLocation()
  const navigate = useNavigate();
  const [reportList, setReportList] = useState([]);
  const [reportListLoading, setReportListLoading] = useState(false);
  const statusOptions = [
    { value: "", label: translations[langMode].SelectDateTime },
    { value: "Active", label: translations[langMode].active },
    { value: "Assigned", label: translations[langMode].assigned },
    { value: "In_Progress", label: translations[langMode].progress },
    { value: "Completed", label: translations[langMode].completed },
    { value: "On_Hold", label: translations[langMode].onhold },
  ];

  const handleOptionChange = (event) => {
    setSelectedOption(event.target.value);
  };

    const handleDownload = async () => {
    try {
      await downloadFiltration(selectedOption);
      if (keepOpenAfterDownload) {
        // Report is queued asynchronously — keep the modal open and refresh
        // the Recent Reports list so the new request shows immediately.
        loadRecentReports();
      } else {
        setDownloadModal(false);
      }
    } catch (error) {
      console.error("Error during download", error);
    }
  };

  const loadRecentReports = async () => {
    setReportListLoading(true);
    try {
      const { reportRequestList } = await fetchReportList(0, 10);
      setReportList(reportRequestList || []);
    } catch (error) {
      console.error("Error fetching recent reports", error);
    } finally {
      setReportListLoading(false);
    }
  };

  useEffect(() => {
    if (!downloadModal) return;

    loadRecentReports();
    const pollInterval = setInterval(() => {
      setReportList((current) => {
        // Every report ends up READY or FAILED, so keep polling only while
        // some row is still IN_PROGRESS (see getReportStatus).
        if (current.some(isReportInProgress)) {
          loadRecentReports();
        }
        return current;
      });
    }, 8000);

    return () => clearInterval(pollInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadModal]);

  return (
    <Transition.Root show={downloadModal} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        initialFocus={cancelButtonRef}
        onClose={() => setDownloadModal(false)}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className={`fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity`}
          />
        </Transition.Child>
        <div className="fixed inset-0 z-50 w-screen overflow-y-auto">
          <div className="flex min-h-full justify-center p-2 sm:p-4 text-center items-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                className={`relative flex flex-col max-h-[85vh] sm:max-h-[90vh] w-full max-w-lg rounded-xl bg-white shadow-2xl transition-all
  ${theme === "dark" ? "dark-mode" : theme === "high-contrast" ? "high-contrast" : ""}`}
              >
                {isLoading && <Spinner />}

                {/* Header */}
                <div className="flex shrink-0 items-center justify-between px-4 sm:px-6 py-3.5 sm:py-4 border-b dark:border-gray-700">
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                    {translations[langMode].downloadtask}
                  </h3>
                  <ModalCloseButton onClick={() => setDownloadModal(false)} />
                </div>

                {/* Body */}
                <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-4 sm:py-5 space-y-4 sm:space-y-5 text-left">
                  {/* Dashboard specific */}
                  {pathname.pathname === "/dashboard" && (
                    <>
                      {type === "priority" ? (
                        <div>
                          <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">
                            {translations[langMode]?.selectPriorityLabel ?? "Select Priority"}
                          </label>

                          <div className="flex gap-3">
                            {[
                              { id: "Low_Priority", label: translations[langMode].lowPriority },
                              { id: "Medium_Priority", label: translations[langMode].mediumPriority },
                              { id: "High_Priority", label: translations[langMode].highPriority },
                            ].map((item) => (
                              <label
                                key={item.id}
                                className={`cursor-pointer rounded-lg border px-4 py-2 text-sm
                  ${priority === item.id
                                    ? "bg-blue-600 text-white border-blue-600"
                                    : "border-gray-300 text-gray-700 dark:text-gray-200 dark:border-gray-600"
                                  }`}
                              >
                                <input
                                  type="radio"
                                  name="priority"
                                  value={item.id}
                                  checked={priority === item.id}
                                  onChange={(e) => setPriority(e.target.value)}
                                  className="hidden"
                                />
                                {item.label}
                              </label>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div>
                          <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">
                            {translations[langMode].selecttask}
                          </label>
                          <ReactSelector
                            options={statusOptions}
                            value={
                              statusOptions.find((option) => option.value === status) ||
                              statusOptions[0]
                            }
                            onChange={(selected) => setStatus(selected?.value || "")}
                            placeholder={translations[langMode].SelectDateTime}
                            height="40px"
                            isClearable={false}
                            menuPortalTarget={document.body}
                          />
                        </div>
                      )}
                    </>
                  )}

                  {/* Activity Selection */}
                  <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
                    <p className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-200">
                      {translations[langMode]?.activitySelection ?? "Activity Selection"}
                    </p>

                    <div className="flex flex-col gap-3">
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="radio"
                          name="activityOption"
                          value="all"
                          checked={selectedOption === "all"}
                          onChange={handleOptionChange}
                          className="accent-blue-600"
                        />
                        {translations[langMode].seectallactivty}
                      </label>

                      <label className="flex items-center text-start gap-2 text-sm cursor-pointer">
                        <input
                          type="radio"
                          name="activityOption"
                          value="particular"
                          checked={selectedOption === "particular"}
                          onChange={handleOptionChange}
                          className="accent-blue-600"
                        />
                        {translations[langMode].selectpartcular}
                      </label>
                    </div>
                  </div>

                  {/* Recent Reports */}
                  <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
                    <div className="mb-3 flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                        {translations[langMode].recentReports}
                      </p>
                      <button
                        type="button"
                        onClick={loadRecentReports}
                        disabled={reportListLoading}
                        className="text-gray-400 hover:text-gray-600 disabled:opacity-50 dark:hover:text-gray-200"
                      >
                        <RefreshCw
                          className={`h-4 w-4 ${reportListLoading ? "animate-spin" : ""}`}
                        />
                      </button>
                    </div>

                    <div className="mb-3 flex items-start gap-1 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-900/50 dark:bg-amber-950/30">
                      <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-500 dark:text-amber-400" />
                      <p className="text-xs leading-5 text-amber-800 dark:text-amber-300">
                        <span className="font-semibold">{translations[langMode].noteLabel}</span>{" "}
                        {translations[langMode].reportGeneratingNote}
                      </p>
                    </div>

                    <div className="max-h-48 space-y-2 overflow-y-auto">
                      {reportListLoading && reportList.length === 0 ? (
                        <div className="flex justify-center py-4">
                          <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
                        </div>
                      ) : reportList.length === 0 ? (
                        <p className="py-2 text-sm text-gray-500 dark:text-gray-400">
                          {translations[langMode].noreportdataavb}
                        </p>
                      ) : (
                        reportList.map((report) => (
                          <div
                            key={report.id}
                            className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 rounded-md border border-gray-100 p-2.5 sm:px-3 sm:py-2 dark:border-gray-700"
                          >
                            <div className="min-w-0 flex-1">
                              <p className="break-words text-sm font-medium text-gray-800 dark:text-gray-200">
                                {report.workflowNames}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {formatRequestedAt(report.createdAt)}
                              </p>
                            </div>
                            {getReportStatus(report) === REPORT_STATUS.FAILED ? (
                              <ReportFailedStatus
                                errorMessage={report.errorMessage}
                                label={
                                  isPerformanceReport(report)
                                    ? translations[langMode].performancereport
                                    : translations[langMode].exportreport
                                }
                                variant="chip"
                                className="self-start sm:self-auto shrink-0"
                              />
                            ) : getReportStatus(report) === REPORT_STATUS.NO_RECORDS ? (
                              <ReportNoRecordsStatus
                                message={report.errorMessage}
                                label={
                                  isPerformanceReport(report)
                                    ? translations[langMode].performancereport
                                    : translations[langMode].exportreport
                                }
                                variant="chip"
                                className="self-start sm:self-auto shrink-0"
                              />
                            ) : getReportStatus(report) === REPORT_STATUS.READY ? (
                              <a
                                href={getReportLink(report)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex self-start sm:self-auto shrink-0 items-center rounded-lg bg-[#236DB4] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#1d5a96]"
                              >
                                {translations[langMode].download}
                              </a>
                            ) : (
                              <span className="inline-flex self-start sm:self-auto shrink-0 items-center rounded-lg bg-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 dark:bg-gray-600 dark:text-gray-300">
                                {translations[langMode].reportGenerating}
                              </span>
                            )}
                          </div>
                        ))
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        setDownloadModal(false);
                        navigate("/export-report-and-data");
                      }}
                      className="mt-3 text-xs font-medium text-blue-600 hover:underline dark:text-blue-400"
                    >
                      {translations[langMode].viewAllReports}
                    </button>
                  </div>

                  {warningMessage && (
                    <div
                      className="flex items-start gap-1 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-900/50 dark:bg-amber-950/30"
                      data-testid="download-modal-warning"
                    >
                      <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-500 dark:text-amber-400" />
                      <p className="text-xs leading-5 text-amber-800 dark:text-amber-300">
                        {warningMessage}
                      </p>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="flex shrink-0 justify-end items-center gap-2.5 px-4 sm:px-6 py-3 border-t dark:border-gray-700 bg-white dark:bg-slate-900 rounded-b-xl">
                  <button data-button-intent="danger"
                    type="button"
                    onClick={() => setDownloadModal(false)}
                    className="inline-flex h-8 items-center justify-center rounded-lg bg-[#FF0000] px-3.5 text-sm font-medium text-white transition-colors hover:bg-[#e60000] focus:outline-none focus:ring-2 focus:ring-red-300"
                  >
                    {translations[langMode].cancle}
                  </button>
                  <button data-button-intent="primary"
                    type="button"
                    onClick={handleDownload}
                    disabled={isLoading}
                    className="inline-flex h-8 items-center justify-center rounded-lg bg-[#236DB4] px-3.5 text-sm font-medium text-white transition-colors hover:bg-[#1d5a96] focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {translations[langMode].download}
                  </button>
                </div>
              </Dialog.Panel>

            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
